import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RotateCcw, Plus, Trash2, AlertCircle, CheckCircle, Loader, X } from 'lucide-react';
import {
  useGetEmailTemplateQuery,
  useCreateOrUpdateEmailTemplateMutation,
  useResetEmailTemplateMutation,
  EmailFormat,
} from '../../store/emailTemplateApi';
import {
  TEMPLATE_CONFIGS,
  type EditableTemplateType,
} from './templateSections';
import { assembleEmailHtml, type SectionValues } from './htmlAssembler';
import { parseSectionsFromHtml, getDefaultSections } from './sectionParser';
import VariableChipBar from './VariableChipBar';

interface InlineEmailEditorProps {
  templateType: EditableTemplateType;
  onSaved: () => void;
  onCancel: () => void;
  /**
   * Per-send override mode. When provided, "Save" commits the edited subject+body
   * via this callback INSTEAD of persisting to the template. Also hides the
   * Reset button and swaps the Save label to "Apply to this send".
   */
  onSaveOverride?: (subject: string, body: string) => void;
  initialSubject?: string;
  initialBody?: string;
}

const InlineEmailEditor = ({
  templateType,
  onSaved,
  onCancel,
  onSaveOverride,
  initialSubject,
  initialBody,
}: InlineEmailEditorProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings' });
  const [subject, setSubject] = useState('');
  const [sections, setSections] = useState<SectionValues>(() => getDefaultSections(templateType));
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Track which field is focused for variable insertion
  const focusedFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const config = TEMPLATE_CONFIGS[templateType];

  // API hooks
  const { data: template, isLoading: isLoadingTemplate } = useGetEmailTemplateQuery(templateType);
  const [createOrUpdate, { isLoading: isSaving }] = useCreateOrUpdateEmailTemplateMutation();
  const [resetTemplate, { isLoading: isResetting }] = useResetEmailTemplateMutation();

  // Load template data when template changes. In per-send mode with an existing
  // override, prefer the override over the template so edits round-trip correctly.
  useEffect(() => {
    if (onSaveOverride && (initialSubject || initialBody)) {
      if (initialSubject) setSubject(initialSubject);
      if (initialBody) {
        const parsed = parseSectionsFromHtml(initialBody, templateType);
        setSections(parsed);
      }
      return;
    }
    if (template) {
      setSubject(template.subject);
      const parsed = parseSectionsFromHtml(template.body, templateType);
      setSections(parsed);
    } else if (!isLoadingTemplate) {
      const defaults = getDefaultSections(templateType);
      setSubject(config.defaultSubject);
      setSections(defaults);
    }
  }, [template, templateType, isLoadingTemplate, config.defaultSubject, onSaveOverride, initialSubject, initialBody]);

  // Section update helpers
  const updateGreeting = (value: string) => {
    setSections(prev => ({ ...prev, greeting: value }));
  };

  const updateParagraph = (index: number, value: string) => {
    setSections(prev => {
      const updated = [...prev.bodyParagraphs];
      updated[index] = value;
      return { ...prev, bodyParagraphs: updated };
    });
  };

  const addParagraph = () => {
    if (sections.bodyParagraphs.length < config.maxParagraphs) {
      setSections(prev => ({
        ...prev,
        bodyParagraphs: [...prev.bodyParagraphs, ''],
      }));
    }
  };

  const removeParagraph = (index: number) => {
    if (sections.bodyParagraphs.length > 1) {
      setSections(prev => ({
        ...prev,
        bodyParagraphs: prev.bodyParagraphs.filter((_, i) => i !== index),
      }));
    }
  };

  const updateCtaLabel = (value: string) => {
    setSections(prev => ({ ...prev, ctaLabel: value }));
  };

  const updateClosingText = (value: string) => {
    setSections(prev => ({ ...prev, closingText: value }));
  };

  // Insert variable at cursor position in focused field
  const handleInsertVariable = (token: string) => {
    const field = focusedFieldRef.current;
    if (!field) return;

    const start = field.selectionStart ?? field.value.length;
    const end = field.selectionEnd ?? start;
    const newValue = field.value.slice(0, start) + token + field.value.slice(end);

    const fieldName = field.dataset.fieldName;
    const fieldIndex = field.dataset.fieldIndex;

    if (fieldName === 'subject') {
      setSubject(newValue);
    } else if (fieldName === 'greeting') {
      updateGreeting(newValue);
    } else if (fieldName === 'paragraph' && fieldIndex !== undefined) {
      updateParagraph(parseInt(fieldIndex), newValue);
    } else if (fieldName === 'ctaLabel') {
      updateCtaLabel(newValue);
    } else if (fieldName === 'closingText') {
      updateClosingText(newValue);
    }

    // Restore focus and cursor position after React re-render
    requestAnimationFrame(() => {
      field.focus();
      const newCursor = start + token.length;
      field.setSelectionRange(newCursor, newCursor);
    });
  };

  // Save handler
  const handleSave = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (!subject.trim()) {
      setErrorMessage(t('emailEditor.subjectEmpty'));
      return;
    }

    try {
      const fullHtml = assembleEmailHtml(templateType, sections);
      if (onSaveOverride) {
        // Per-send mode — write to parent form state, never hit the template API.
        onSaveOverride(subject, fullHtml);
        onSaved();
        return;
      }
      await createOrUpdate({
        type: templateType,
        subject,
        body: fullHtml,
        format: EmailFormat.HTML,
      }).unwrap();
      setSuccessMessage(t('emailEditor.savedSuccess'));
      setTimeout(() => {
        onSaved();
      }, 800);
    } catch (err: unknown) {
      const apiErr = err as { data?: { error?: string; message?: string } };
      setErrorMessage(apiErr?.data?.error || apiErr?.data?.message || t('emailEditor.saveFailed'));
    }
  };

  // Reset handler
  const handleReset = async () => {
    if (!window.confirm(t('emailEditor.resetConfirm'))) return;

    setErrorMessage('');
    setSuccessMessage('');

    try {
      const resetData = await resetTemplate(templateType).unwrap();
      setSubject(resetData.subject);
      const parsed = parseSectionsFromHtml(resetData.body, templateType);
      setSections(parsed);
      setSuccessMessage(t('emailEditor.resetSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: unknown) {
      const apiErr = err as { data?: { error?: string; message?: string } };
      setErrorMessage(apiErr?.data?.error || apiErr?.data?.message || t('emailEditor.resetFailed'));
    }
  };

  // Common field props for tracking focus
  const fieldProps = (fieldName: string, fieldIndex?: number) => ({
    'data-field-name': fieldName,
    'data-field-index': fieldIndex?.toString(),
    onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      focusedFieldRef.current = e.target;
    },
  });

  return (
    <div className="space-y-5">
      {/* Amber info banner */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{onSaveOverride ? t('emailEditor.perSendBanner') : t('emailEditor.infoBanner')}</span>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {errorMessage}
        </div>
      )}

      {isLoadingTemplate ? (
        <div className="flex items-center justify-center py-16">
          <Loader className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : (
        <>
          {/* Variable Chips */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              {t('emailEditor.insertVariables')}
            </label>
            <VariableChipBar
              variables={config.variables}
              onInsert={handleInsertVariable}
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('emailEditor.subjectLine')}</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              placeholder={t('emailEditor.subjectPlaceholder')}
              {...fieldProps('subject')}
            />
          </div>

          {/* Greeting */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('emailEditor.greeting')}</label>
            <input
              type="text"
              value={sections.greeting}
              onChange={(e) => updateGreeting(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              placeholder={t('emailEditor.greetingPlaceholder')}
              {...fieldProps('greeting')}
            />
          </div>

          {/* Body Paragraphs */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                {t('emailEditor.bodyParagraphs')}
              </label>
              {sections.bodyParagraphs.length < config.maxParagraphs && (
                <button
                  type="button"
                  onClick={addParagraph}
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('emailEditor.addParagraph')}
                </button>
              )}
            </div>
            <div className="space-y-3">
              {sections.bodyParagraphs.map((paragraph, index) => (
                <div key={index} className="relative">
                  <textarea
                    value={paragraph}
                    onChange={(e) => updateParagraph(index, e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-y"
                    placeholder={t('emailEditor.paragraphPlaceholder', { number: index + 1 })}
                    {...fieldProps('paragraph', index)}
                  />
                  {sections.bodyParagraphs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeParagraph(index)}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title={t('emailEditor.removeParagraph')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button Label (conditional) */}
          {config.ctaLinkVariable && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {t('emailEditor.buttonLabel')}
              </label>
              <input
                type="text"
                value={sections.ctaLabel}
                onChange={(e) => updateCtaLabel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                placeholder={t('emailEditor.buttonLabelPlaceholder')}
                {...fieldProps('ctaLabel')}
              />
              <p className="text-xs text-gray-400 mt-1">
                {t('emailEditor.buttonLinksTo')} <code className="bg-gray-100 px-1 rounded">{`{{${config.ctaLinkVariable}}}`}</code> {t('emailEditor.automatic')}
              </p>
            </div>
          )}

          {/* Closing / Warning Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t('emailEditor.closingText')}
            </label>
            <textarea
              value={sections.closingText}
              onChange={(e) => updateClosingText(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-y"
              placeholder={t('emailEditor.closingPlaceholder')}
              {...fieldProps('closingText')}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {onSaveOverride
                ? t('emailEditor.applyToThisSend')
                : (isSaving ? t('common:saving') : t('emailEditor.saveTemplate'))}
            </button>
            {!onSaveOverride && (
              <button
                type="button"
                onClick={handleReset}
                disabled={isResetting}
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
              >
                {isResetting ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                {t('emailEditor.reset')}
              </button>
            )}
            <button
              type="button"
              onClick={onCancel}
              className="ml-auto flex items-center gap-1.5 px-3 py-2.5 text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
            >
              <X className="w-4 h-4" />
              {t('common:cancel')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default InlineEmailEditor;
